import dayjs from "dayjs";
import humps from "humps";
import { createUUID } from "mocks/data/utils";
import { connect, ConnectionOptions, DebugEvents, Events, headers, NatsConnection, StringCodec, Subscription } from "nats.ws";
import farmSo from "stores/farm";
import metaSo from "stores/metadata";
import natsSo, { NATS_STATUS } from "stores/nats";
import { Adapter, PlcRef } from "stores/plc/Adapter";
import userSo, { LOGIN_TYPE } from "stores/user";
import valuesSo from "stores/values";
import { Nats } from "types/Service";
import authService from "./Auth";
import { NatsKV } from "./NatsKV";



/** opzioni di connessione */
let options: ConnectionOptions = null
let responseTimeout: number = 10000
/** la connessione a NATS */
let nc: NatsConnection = null
/** numero di tentativi di riconnessione */
let attempts = 0
/** id timeout per la riconnessione */
let reconnectTimeout = null
/** indic che non si deve riconnettere */
let reconnectDisabled = false
/** l'oggetto delle subscription */
let sub: Subscription
/** ultimo host a cui s'e' connesso (se non connesso = null) */
let lastHostId: string

/** dove sono pubblicati i COMMAND vale a dire le modifiche da parte del FE*/
let publishSubjectTemplate = null
/** stringa di sottoscrizione */
let subscribeSubject = null

/** indicates the subject to use in NATS to requests */
export enum NATS_META_REQUESTS {
	INDEX = "services.plc-console.req.metadata.get",
	SAVE = "services.plc-console.req.metadata.update",
	REMOVE = "services.plc-console.req.metadata.delete",
}

let callbackConnection: () => void = null

function getAuthHeaders() {
	const messageHeaders = headers()

	if (userSo.state.loginType == LOGIN_TYPE.LEGACY) return messageHeaders

	const bearer = authService.getBearerToken()
	if (!!bearer) {
		messageHeaders.set("Authorization", bearer)
	}

	const user = userSo.state.current
	if (!!user) {
		const username = user.email.split("@")[0]
		const userJson = {
			username: username ?? user.name,
			name: user.name,
			email: user.email,
		}
		messageHeaders.set("ZeroAuth", JSON.stringify(userJson))
	}

	return messageHeaders
}



/** inizia una connessione con NATS */
export async function NATSConnect(
	nats: Nats,
	onConnection?: () => void
) {
	let hosts = nats.host ?? window.location.hostname;
	if (typeof hosts == "string") hosts = [hosts];
	const prot = (nats.enable_tls === true || window.location.protocol === "https:") ? "wss:" : "ws:"
	const urls = hosts.map(host => `${prot}//${host}:${nats.port ?? 8022}`)
	const hostId = `${urls[0]}@${nats.username ?? ""}`
	if (hostId == lastHostId) return

	subscribeSubject = nats.subscribe
	publishSubjectTemplate = nats.publish
	callbackConnection = onConnection

	options = {
		name: `PLC-CONSOLE ${createUUID()}`,
		servers: urls,
		user: nats.username,
		pass: nats.password,
		reconnect: true,
		reconnectTimeWait: 1000,
		maxReconnectAttempts: -1,
		timeout: 3000,
		maxPingOut: 3,
		pingInterval: 3000,
		...nats.username ? { inboxPrefix: `_INBOX.${nats.username}` } : {}
	}
	if (!!nats.response_timeout) responseTimeout = nats.response_timeout

	// se sono in dev preferisco non avere questi limiti
	if (import.meta.env.DEV) {
		options.timeout = undefined
		options.maxPingOut = undefined
		options.pingInterval = undefined
	}
	if (import.meta.env.DEV && import.meta.env.VITE_NATS_MOCK == "true") {
		options = {
			servers: "ws://localhost:8081",
		}
	}
	/** provo a connettermi */
	if (!!nc) await NATSClose()
	attempts = 0
	reconnectDisabled = false
	tryConnect()
}


/** prova a connettersi. se non riesce ci riprova */
async function tryConnect() {

	if (!!nc) await NATSClose()
	tryConnectStop()
	if (reconnectDisabled) return

	try {
		log("NATS", `try connect::${attempts}::servers::${options.servers}`)
		nc = await connect(options)
	} catch (err) {
		nc = null
		attempts++
		natsSo.changeStatus(attempts > 5 ? NATS_STATUS.OFFLINE : NATS_STATUS.RETRY)
		reconnectTimeout = setTimeout(tryConnect, options.reconnectTimeWait)
		return
	}

	// START SIMULATOR
	if (import.meta.env.DEV && import.meta.env.VITE_NATS_MOCK == "true") {
		const { start } = await import("./mock/simulator")
		start(nc)
	}

	attempts = 0
	reconnectDisabled = false
	lastHostId = `${options.servers[0] as string}@${options.user ?? ""}`
	natsSo.changeStatus(NATS_STATUS.ONLINE)

	callbackConnection?.()
	callbackConnection = null

	listenerStatus()
	listenerClosed()
	listenerSubscribe()
}

/** annulla l'ultimo TRY-CONNECTION */
function tryConnectStop() {
	if (!!reconnectTimeout) clearTimeout(reconnectTimeout)
}

/**chiamata su connessione avvenuta determina lo stato della connessione DOPO che NATS si è collegato correttamente */
async function listenerStatus() {

	log("NATS", `connected::${options.name}::server::${nc.getServer()}`);
	// inserisco i servizi disponibili che trovo in edge-config
	const subjects = [...new Set(farmSo.state.select?.edgeServicesConfig?.components.flatMap(c => c?.subjects ?? []))]
	const services = subjects.reduce((acc, s) => (acc[s] = { code: s, values: {} }, acc), {})
	valuesSo.state.services = services
	valuesSo.buffer = {}

	for await (const s of nc.status()) {
		if (s.type == DebugEvents.Reconnecting) {
			attempts++
			natsSo.changeStatus(attempts > 5 ? NATS_STATUS.OFFLINE : NATS_STATUS.RETRY)
			log("NATS", `try connect::${attempts}::servers::${options.servers}`)
		} else if (s.type == Events.Reconnect) {
			attempts = 0
			natsSo.changeStatus(NATS_STATUS.ONLINE)
			log("NATS", `re-connected::${options.name}::server::${nc.getServer()}`);
		}
	}
}

/** il canale NATS chiude */
async function listenerClosed() {
	const err = await nc.closed()
	nc = null
	if (err) {
		console.error('[NATS]::connection error::', err);
	} else {
		log("NATS", 'closed::OK')
		natsSo.setStatus(NATS_STATUS.OFFLINE)
		valuesSo.setServices({})
	}
}

/** ricevo un messaggio da NATS */
async function listenerSubscribe() {
	sub = nc.subscribe(subscribeSubject)
	for await (const m of sub) {
		const subject = m.subject
		const serviceCode = subject.split(".")[1]
		try {
			let values = m.json() as { [key: string]: number }
			valuesSo.updateValues({ serviceCode, values })
		} catch (error) {
			let msg = m.string()
			console.error(msg)
			console.error(error?.chainedError?.message)
		}
	}
}

/**
 * pubblico un "value" in un service_code + alias diretto alla coda NATS (e quindi al PLC)
 * Per quanto riguarda i "sinottici" il serviceCode corrisponde a: `<GrowUnit>.growUnitCode`
 */
export function NATSPublish(serviceCode: string, alias: string, value: any, adapter?: PlcRef) {

	if (!userSo.plcAccess() || userSo.isReader() || !nc) return false

	if (!!adapter) {
		const ref = Adapter.Get(adapter.context, adapter.path, adapter.params)
		alias = ref.alias
		serviceCode = ref.code
	}
	if (!alias || value == null || value.length == 0) return false
	if (!serviceCode) serviceCode = valuesSo.state.serviceCodeSel

	if (typeof value == "boolean") value = value ? 1 : 0

	let valueName = metaSo.getMetadataByAlias({ serviceCode, alias })?.name ?? alias
	const json = `{"${valueName}": ${value.toString()}}`

	log("NATS", `publish::${serviceCode}::${alias}`, json)

	const subject = publishSubjectTemplate.replace(/\*/g, serviceCode)
	nc.publish(subject, json, { headers: getAuthHeaders() })
	return true
}

/**
 * Effettua una REQUEST tramite NATS
 */
export async function NATSRequest(subject: string, data?: any) {
	const sc = StringCodec();
	const encoded = sc.encode(JSON.stringify(data ? humps.decamelizeKeys(data) : {}));
	log("NATS", `request::subject:${subject}`);
	let msg: any
	try {
		msg = await nc.request(subject, encoded, {
			timeout: responseTimeout,
			headers: getAuthHeaders(),
		});
	} catch (err) {
		log("NATS", `request::subject:${subject}::error`, err);
		throw err;
	}
	const resp = humps.camelizeKeys(JSON.parse(sc.decode(msg.data)));
	log("NATS", "request::response:", resp);
	return resp;
}


/** chiudo tutto! */
export async function NATSClose() {
	reconnectDisabled = true
	tryConnectStop()

	natsSo.setStatus(NATS_STATUS.OFFLINE)
	// azzero il service dei valori
	clearTimeout(valuesSo.bufferTimeout)
	valuesSo.bufferTimeout = null
	valuesSo.buffer = {}
	valuesSo.setServices({})

	// STOP SIMULATOR
	if (import.meta.env.DEV && import.meta.env.VITE_NATS_MOCK == "true") {
		const { stop } = await import("./mock/simulator")
		stop()
	}

	await nc?.close()
	nc = null
	lastHostId = null
	log("NATS", "::connection deleted")
}

/** 
 * restituisce NatsKV collegato ad uno specifico Bucket dalla connessione "nc" 
 */
export async function getNatsKV(bucketName: string) {
	if (!nc) return null
	const natsKV = new NatsKV(nc)
	await natsKV.selectBucket(bucketName)
	return natsKV
}


/** log */
function log(tag: string, msg: string, data?: any) {
	console.info(
		`%c[${tag}]::[${dayjs().format('HH:mm:ss.SSS')}]%c::${msg}`,
		'color: yellow;',
		'',
	)
	if (data !== undefined) console.info(data);
}